import { useState } from "react";
import { ArrowUp } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

// "Ask anything about San Juanillo" input (Ask page only). Flow: submit the
// question -> optionally leave an email to get notified when it's answered ->
// field refreshes so they can ask another right away.
export default function AskBar({ prominent = false }) {
  const { t } = useI18n();
  const [step, setStep] = useState("ask"); // ask | email
  const [question, setQuestion] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [qid, setQid] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const reset = (msg = "") => {
    setStep("ask");
    setQuestion("");
    setEmail("");
    setQid(null);
    setError("");
    setNotice(msg);
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/questions", { question: question.trim(), website });
      setQid(r.data.id);
      setStep("email");
      setNotice("");
    } catch (err) {
      setError(err?.response?.data?.detail || t("errGeneric"));
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      if (qid) await api.post(`/questions/${qid}/email`, { email: email.trim() });
      reset(t("askDoneMini"));
    } catch (err) {
      setError(err?.response?.data?.detail || t("errGeneric"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`ask-bar${prominent ? " is-prominent" : ""}`} data-testid="ask-bar">
      <div className="dd-wrap">
        {step === "ask" ? (
          <form className="ask-form" onSubmit={submitQuestion}>
            <input
              className="ask-input"
              value={question}
              onChange={(e) => { setQuestion(e.target.value); setNotice(""); }}
              placeholder={t("askPlaceholder")}
              maxLength={1000}
              data-testid="ask-input"
            />
            {/* honeypot — hidden from humans */}
            <input
              type="text"
              className="ask-hp"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <button
              type="submit"
              className="ask-submit"
              disabled={busy || !question.trim()}
              aria-label={t("askButton")}
              data-testid="ask-submit"
            >
              <ArrowUp size={18} strokeWidth={2.5} />
            </button>
          </form>
        ) : (
          <div className="ask-email-step">
            <div className="ask-notify-label" data-testid="ask-notify-prompt">{t("askNotifyPrompt")}</div>
            <form className="ask-form" onSubmit={submitEmail}>
              <input
                className="ask-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("askEmailPh")}
                data-testid="ask-email-input"
              />
              <button
                type="submit"
                className="ask-submit ask-submit-wide"
                disabled={busy || !email.trim()}
                data-testid="ask-email-submit"
              >
                {busy ? "…" : t("askNotifyBtn")}
              </button>
            </form>
            <button type="button" className="ask-skip" onClick={() => reset()} data-testid="ask-skip-btn">
              {t("askSkip")}
            </button>
          </div>
        )}
        {notice ? <div className="ask-notice" data-testid="ask-notice">{notice}</div> : null}
        {error ? <div className="ask-error" data-testid="ask-error">{error}</div> : null}
      </div>
    </section>
  );
}
